import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styles from 'feature/receipt/index.module.sass';
import PageLayout from 'layout/page';
import { useReceiptContext, INITIALIZE, CHANGE_RECEIPT_TYPE, CHANGE_PAYLOAD } from 'feature/receipt/use-receipt';
import iconRefueling from 'assets/main-refueling.svg';
import iconParking from 'assets/main-parking.svg';
import iconOnStreetParking from 'assets/main-on-street-parking.svg';
import Button from 'components/button';
import FormInput from 'components/form-input';
import {
  isReceiptB2b,
  isReceiptCarrierCitizenDigitalNumber,
  isReceiptCarrierMobileBarcode,
} from 'helpers/rule-validation';
import loveCodeData from 'json/love_code';
import { PATH } from 'config';
import classnames from 'classnames/bind';
import { useAppContext } from 'use-app';
import iconInfo from 'assets/info-outlined.svg';
import { config } from 'config';
import ButtonDialog from 'components/button-dialog';
import amplitude from 'service/amplitude';
import appLoader from 'distributor/app-loader';
import taxIdNumberLogicStrategy from './b2b-logic-strategy';

const cx = classnames.bind(styles);

const ReceiptSelector = ({ data }) => {
  const { state, dispatch } = useReceiptContext();
  const history = useHistory();

  if (!state.type) return <></>;

  const onSelectReceipt = e => {
    const type = data.find(x => x.key === e.target.value).name;
    amplitude.receipt_select_onClick(type);
    dispatch({ type: CHANGE_RECEIPT_TYPE, payload: e.target.value });
  };

  let currentData = data.find(d => d.key === state.type);

  const Form = () => {
    if (state.type === 'carrier_member') return '';

    if (state.type === 'donate') {
      const onSelect = () => history.push(PATH.RECEIPT_DONATE);
      const loveCode = loveCodeData.find(x => x.love_code === state.loveCode);
      return (
        <div className={styles['donate-card']} onClick={onSelect}>
          <div className={styles.content}>
            <div className={styles.code}>愛心碼 {loveCode.love_code}</div>
            <div className={styles.name}>{loveCode.name}</div>
          </div>
          <div className={styles.choose}>選擇</div>
        </div>
      );
    }

    return (
      <FormInput
        preValue={currentData.preValue}
        inputMode={currentData.input.inputMode}
        className={styles.input}
        value={currentData.input.value}
        maxLength={currentData.input.maxLength}
        placeholder={currentData.input.placeHolder}
        digits={currentData.input.digits}
        onTextChanged={value =>
          dispatch({ type: CHANGE_PAYLOAD, payload: { [currentData.input.label]: value, errorMessage: '' } })
        }
      />
    );
  };

  const receiptFromClass = cx({
    'receipt-form': true,
    [state.type]: true,
    error: state.errorMessage,
  });

  return (
    <>
      <div className={receiptFromClass}>
        <div className={styles.select}>
          <select onChange={onSelectReceipt} value={state.type}>
            {data.map(receipt => {
              return (
                <option value={receipt.key} key={receipt.key}>
                  {receipt.name}
                </option>
              );
            })}
          </select>
        </div>
        {Form()}
      </div>
      {state.errorMessage && <div className={styles['error-message']}>{state.errorMessage}</div>}
      <div className={styles.hint}>{currentData.hint}</div>
    </>
  );
};

const Receipt = () => {
  const { state: appState } = useAppContext();
  const { state, dispatch, updateUserReceipt } = useReceiptContext();
  const [downloadDialog, setDownloadDialog] = useState(false);
  const location = useLocation();
  const { isUsingLegacyReceiptWording } = appState.bootstraps;
  const { mainFeaturesList } = appLoader.configData;

  useEffect(() => {
    amplitude.receipt_pageview();
  }, []);

  const receiptData = [
    {
      key: 'carrier_member',
      name: '會員載具',
      hint: '發票中獎將以電子信件通知您前往超商列印發票。',
    },
    {
      key: 'carrier_mobile_barcode',
      preValue: '/',
      name: '手機條碼載具',
      input: {
        label: 'barcode',
        value: state.barcode,
        maxLength: 8,
        placeHolder: '輸入載具編號',
      },
      hint: '發票將儲存至您的手機載具中，中獎將由電子發票整合平台通知兌獎。',
    },
    {
      key: 'donate',
      name: '發票捐贈',
      hint: '發票將捐贈給您所選擇的社福團體。',
    },
    {
      key: 'b2b',
      name: '三聯式電子發票',
      input: {
        label: 'ubn',
        inputMode: 'numeric',
        value: state.ubn,
        maxLength: 8,
        placeHolder: '輸入統一編號',
        digits: '[0-9]',
      },
      hint: '發票將開立統一編號並寄送至會員信箱。部分現場索取發票加油站，付款時請告知站員統編。',
    },
    {
      key: 'carrier_citizen_digital',
      name: '自然人憑證載具',
      input: {
        label: 'citizenDigitalNumber',
        value: state.citizenDigitalNumber,
        maxLength: 16,
        placeHolder: '輸入憑證編號',
      },
      hint: '發票將儲存至您的自然人憑證載具中，中獎將由電子發票整合平台通知兌獎。',
    },
  ];

  useEffect(() => {
    if (location.state?.from === PATH.RECEIPT_DONATE) return;
    dispatch({ type: INITIALIZE });
  }, [location]);

  const onSave = () => {
    amplitude.receipt_save_onClick();
    let currentData = receiptData.find(d => d.key === state.type);

    const validation = () => {
      if (state.type === 'donate' || state.type === 'carrier_member') return;

      if (currentData.input.value === '') {
        return '此為必填';
      }

      switch (state.type) {
        case 'carrier_mobile_barcode':
          return !isReceiptCarrierMobileBarcode(`/${state.barcode}`) ? '格式錯誤' : '';
        case 'b2b':
          if (!isReceiptB2b(state.ubn)) return '格式錯誤';
          if (!taxIdNumberLogicStrategy(state.ubn)) return '統一編號輸入錯誤，請重新檢查';
          return '';
        case 'carrier_citizen_digital':
          return !isReceiptCarrierCitizenDigitalNumber(state.citizenDigitalNumber) ? '格式錯誤' : '';
        default:
          return '';
      }
    };

    if (validation()) {
      return dispatch({ type: CHANGE_PAYLOAD, payload: { errorMessage: validation() } });
    }

    const payload = () => {
      switch (state.type) {
        case 'carrier_member':
          return { name: appState.me.displayName, phone: appState.me.mobile };
        case 'carrier_mobile_barcode':
          return { barcode: `/${state.barcode}` };
        case 'b2b':
          return { ubn: state.ubn };
        case 'carrier_citizen_digital':
          return { citizen_digital_number: state.citizenDigitalNumber };
        case 'donate':
          return { love_code: state.loveCode };
        default:
          return '';
      }
    };

    updateUserReceipt({ type: state.type, payload: payload() });
  };

  const openDownloadDialog = () => {
    amplitude.receipt_download_onClick();
    setDownloadDialog(true);
  };

  const RefuelingReceiptDescription = () => {
    if (isUsingLegacyReceiptWording) {
      return '現場開立發票';
    }

    return (
      <ul>
        <li>麻吉付：自動存入發票載具；部分現場開立</li>
        <li>快速通：自動存入發票載具</li>
        {mainFeaturesList.includes('barcode-payment') && <li>條碼付：現場開立發票</li>}
      </ul>
    );
  };

  return (
    <>
      <ButtonDialog
        isShow={downloadDialog}
        title="下載報帳明細"
        horizonal
        items={[
          {
            content: '前往網站',
            theme: 'positive',
            onClick: () => {
              amplitude.receipt_download_dialog_forward();
              setDownloadDialog(false);
              window.location.href = config.mochipayIntroUrl;
            },
          },
          {
            content: '返回',
            theme: 'cancel',
            onClick: () => {
              amplitude.receipt_download_dialog_cancel();
              setDownloadDialog(false);
            },
          },
        ]}
      >
        請登入麻吉付網站，前往「消費記錄」頁，再點選列表右上角「下載消費記錄」按鈕並填入統編，匯出消費紀錄。
      </ButtonDialog>
      <PageLayout>
        <div className={styles.label}>發票開立方式</div>
        <div className={styles.block}>
          <div>
            <div className={styles.title}>
              <img src={iconRefueling} />
              加油站
            </div>
            <RefuelingReceiptDescription />
          </div>
          <div>
            <div className={styles.title}>
              <img src={iconParking} />
              停車場
            </div>
            私有場：自動存入發票載具
            <br />
            公有場：政府規費免開發票
          </div>
          <div>
            <div className={styles.title}>
              <img src={iconOnStreetParking} />
              路邊停車
            </div>
            政府規費免開發票
          </div>
        </div>
        <div className={styles.label}>發票載具設定</div>
        <ReceiptSelector data={receiptData} />
        <div className={styles.button}>
          <Button theme="submit-btn" onClick={onSave} loading={state.btnLoading} disabled={state.btnLoading}>
            儲存
          </Button>
        </div>
        <div className={styles.download} onClick={openDownloadDialog}>
          下載報表明細
          <img src={iconInfo} alt="info" />
        </div>
      </PageLayout>
    </>
  );
};

export default Receipt;
